import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { llmModelsApi } from '../../../services/api/llmModels.api';
import { useClickOutside } from '../../../hooks/useClickOutside';

export interface LlmModel {
  id: string;
  label: string;
  // Which picker the model belongs to (see ModelType). Kept an open string
  // so a type the UI doesn't know yet (e.g. a future "video") is simply never
  // shown, rather than failing normalization.
  type: string;
  default: boolean;
}

// A named group of models within a provider (e.g. "Complex work", "Fast"),
// rendered as a sub-heading with its description in the model menu.
export interface LlmTier {
  id: string;
  label: string;
  description: string;
  models: LlmModel[];
}

export interface LlmProvider {
  id: string;
  label: string;
  tiers: LlmTier[];
}

// Every model under a provider, across all its tiers, in API order.
export const getProviderModels = (provider: LlmProvider): LlmModel[] =>
  provider.tiers.flatMap((t) => t.models);

type ModelsStatus = 'loading' | 'ready' | 'error';

// The model families the input bar picks from — each with its own picker
// and its own selection. The chat model is sent as `model`, the image model
// as `image_model` — each only while its picker is showing (see
// utils/chatPayload's buildModelFields).
export type ModelType = 'chat' | 'image';

// A model the API sends without a type is treated as a chat model.
const DEFAULT_MODEL_TYPE: ModelType = 'chat';

// Which model pickers the input bar shows, top to bottom — and so which
// models a message sends. Create Image on its own needs only an image model;
// Create Image alongside anything else (another source, File Upload or Web
// Search) needs both, image above chat; everything else is chat only. A
// hidden picker keeps its pick for when it shows again.
export const getModelPickerTypes = (
  selectedSources: Set<string>,
  { fileUploadMode, webSearch }: { fileUploadMode: boolean; webSearch: boolean },
): ModelType[] => {
  if (!selectedSources.has('image_generation')) return ['chat'];
  const hasOtherSelection = selectedSources.size > 1 || fileUploadMode || webSearch;
  return hasOtherSelection ? ['image', 'chat'] : ['image'];
};

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const nonEmptyString = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

// A malformed entry left out of the list, and why — collected while
// normalizing, so fetchModels can report them all in one warning.
interface DroppedEntry {
  reason: string;
  entry: unknown;
}
type ReportDropped = (reason: string, entry: unknown) => void;

// Turns one raw model entry into an LlmModel, or null if it has no usable
// id. Every other field falls back to a safe value, so a renamed or missing
// field degrades the menu instead of breaking it.
const normalizeModel = (raw: unknown, reportDropped: ReportDropped): LlmModel | null => {
  if (!isObject(raw)) {
    reportDropped('a model that is not an object', raw);
    return null;
  }
  const id = nonEmptyString(raw.id);
  if (!id) {
    reportDropped('a model without an id', raw);
    return null;
  }
  return {
    id,
    label: nonEmptyString(raw.label) ?? id,
    type: nonEmptyString(raw.type) ?? DEFAULT_MODEL_TYPE,
    default: raw.default === true,
  };
};

const normalizeModels = (raw: unknown, reportDropped: ReportDropped): LlmModel[] =>
  asArray(raw).map((m) => normalizeModel(m, reportDropped)).filter((m): m is LlmModel => m !== null);

// Validates the /llm_models response into the providers → tiers → models
// shape the rest of the UI relies on. A provider without `tiers` but with a
// flat `models` list (the older response format) gets a single unlabeled
// tier, which the menu renders without a sub-heading. Anything malformed is
// dropped rather than trusted — an unexpected shape here used to throw
// during render and blank the whole page. Each dropped entry is passed to
// reportDropped.
const normalizeProviders = (data: unknown, reportDropped: ReportDropped): LlmProvider[] => {
  const rawProviders = isObject(data) ? asArray(data.providers) : [];
  return rawProviders.flatMap((raw) => {
    if (!isObject(raw)) {
      reportDropped('a provider that is not an object', raw);
      return [];
    }
    const id = nonEmptyString(raw.id);
    if (!id) {
      reportDropped('a provider without an id', raw);
      return [];
    }
    const tiers: LlmTier[] = Array.isArray(raw.tiers)
      ? raw.tiers.flatMap((tier, i) => {
          if (!isObject(tier)) {
            reportDropped('a tier that is not an object', tier);
            return [];
          }
          return [{
            id: nonEmptyString(tier.id) ?? `tier-${i}`,
            label: nonEmptyString(tier.label) ?? '',
            description: nonEmptyString(tier.description) ?? '',
            models: normalizeModels(tier.models, reportDropped),
          }];
        })
      : [{ id: 'default', label: '', description: '', models: normalizeModels(raw.models, reportDropped) }];
    return [{ id, label: nonEmptyString(raw.label) ?? id, tiers: tiers.filter((t) => t.models.length > 0) }];
  });
};

// Narrows providers down to only the models matching `type`, dropping any
// tier or provider left with zero models — e.g. a provider with no image
// models disappears from the image picker entirely, rather than showing an
// empty group.
const modelsOfType = (providers: LlmProvider[], type: ModelType): LlmProvider[] =>
  providers
    .map((p) => ({
      ...p,
      tiers: p.tiers
        .map((t) => ({ ...t, models: t.models.filter((m) => m.type === type) }))
        .filter((t) => t.models.length > 0),
    }))
    .filter((p) => p.tiers.length > 0);

// Prefers the type's marked default; if none is marked (e.g. no model of
// this type is flagged default yet), falls back to the first model in list
// order so a type is never left with nothing selected.
const findDefaultModelId = (providers: LlmProvider[]): string | null => {
  const models = providers.flatMap(getProviderModels);
  return models.find((m) => m.default)?.id ?? models[0]?.id ?? null;
};

const findProviderIdForModel = (providers: LlmProvider[], modelId: string | null): string | null => {
  if (!modelId) return null;
  return providers.find((p) => getProviderModels(p).some((m) => m.id === modelId))?.id ?? null;
};

const hasModel = (providers: LlmProvider[], modelId: string): boolean =>
  providers.some((p) => getProviderModels(p).some((m) => m.id === modelId));

// A picker's selected model: the one picked for it while that model is still
// in its list, otherwise the list's default. So no pick yet, a pick restored
// before the list has loaded, and a model since removed from the API all
// settle on something valid with no extra bookkeeping. null only while the
// list is empty (still loading, failed, or no models of that type).
const resolveModelId = (providers: LlmProvider[], pickedId: string | null): string | null =>
  pickedId && hasModel(providers, pickedId) ? pickedId : findDefaultModelId(providers);

type ModelIdsByType = Record<ModelType, string | null>;

const NO_PICKS: ModelIdsByType = { chat: null, image: null };

// One model list (GET /llm_models), one independent selection per ModelType.
// Each type's picker only ever shows that type's models, and its selection
// survives the picker being hidden — e.g. the chat model while Create Image
// is on its own — so toggling Create Image never loses either pick.
export const useModelSelector = (isNewChatRef: React.RefObject<boolean>) => {
  const [allProviders, setAllProviders] = useState<LlmProvider[]>([]);
  // What was picked per type (by the user, or restored for a reopened chat);
  // null means "that type's default". Only ever read through
  // resolveModelId, which also checks the pick still exists.
  const [pickedModelIds, setPickedModelIds] = useState<ModelIdsByType>(NO_PICKS);
  const [lockedProviderId, setLockedProviderId] = useState<string | null>(null);
  const [modelsStatus, setModelsStatus] = useState<ModelsStatus>('loading');
  // Which picker's menu is open — at most one at a time — or null.
  const [openModelMenu, setOpenModelMenu] = useState<ModelType | null>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);
  // The button of the picker whose menu is open (ChatInput gives it only to
  // that picker); counts as inside, so clicking it again closes the menu.
  const modelMenuTriggerRef = useRef<HTMLButtonElement>(null);

  // What each picker's menu shows — allProviders narrowed to that type.
  // Default lookup and provider-lock checks use these, never the raw list.
  const providersByType = useMemo<Record<ModelType, LlmProvider[]>>(
    () => ({
      chat: modelsOfType(allProviders, 'chat'),
      image: modelsOfType(allProviders, 'image'),
    }),
    [allProviders]
  );

  const selectedModelIds: ModelIdsByType = {
    chat: resolveModelId(providersByType.chat, pickedModelIds.chat),
    image: resolveModelId(providersByType.image, pickedModelIds.image),
  };

  const fetchModels = useCallback(async () => {
    setModelsStatus('loading');
    try {
      const data = await llmModelsApi.getModels();
      const dropped: DroppedEntry[] = [];
      const fetchedProviders = normalizeProviders(data, (reason, entry) => dropped.push({ reason, entry }));
      // Malformed entries are left out, and the rest still load. They're
      // reported (decided 7 Oct), so a backend shape change shows up in the
      // console instead of a model silently missing from the picker.
      if (dropped.length > 0) {
        console.warn(
          `[model-selector] /llm_models: left out ${dropped.length} malformed ${dropped.length === 1 ? 'entry' : 'entries'}:`,
          dropped,
        );
      }
      setAllProviders(fetchedProviders);
      // Nothing usable came back: treat it like a failed load, so the chat
      // input offers Retry instead of a model picker with nothing in it.
      if (fetchedProviders.every((p) => p.tiers.length === 0)) {
        console.warn('[model-selector] /llm_models returned no usable models:', data);
        setModelsStatus('error');
        return;
      }
      setModelsStatus('ready');
    } catch (e) {
      console.error('[model-selector] fetchModels error:', e);
      setModelsStatus('error');
    }
  }, []);

  useEffect(() => {
    void fetchModels();
  }, [fetchModels]);

  // A click outside the open menu closes it. The open picker's button counts
  // as inside (fix #17): otherwise pressing it closes the menu on mousedown
  // and its click reopens it.
  useClickOutside([modelMenuRef, modelMenuTriggerRef], () => setOpenModelMenu(null), openModelMenu !== null);

  const toggleModelMenu = (type: ModelType) => {
    setOpenModelMenu((open) => (open === type ? null : type));
  };

  const selectModel = (type: ModelType, modelId: string) => {
    setPickedModelIds((picked) => ({ ...picked, [type]: modelId }));
    setOpenModelMenu(null);
  };

  // Puts back the models a reopened chat's latest message used (see
  // chatSelectionsStorage). They're set as that chat's picks and checked
  // like any other pick (resolveModelId): one no longer in the list falls
  // back to the default, and one restored before the list has loaded is
  // applied the moment it arrives.
  const restoreModels = (saved: ModelIdsByType) => {
    setPickedModelIds(saved);
  };

  // Locks the provider of the chat model selected at send-time, but only
  // for brand-new chats — resumed history chats never lock. Idempotent: once
  // locked, further sends in the same chat are no-ops. Only the chat picker
  // is ever locked; the image picker stays free.
  //
  // TEMPORARILY DISABLED — provider locking is commented out for now, so
  // lockedProviderId stays null forever and every model stays selectable
  // regardless of what's already been sent. Uncomment the body below to
  // re-enable the lock-on-first-send behavior.
  const lockProviderForSend = () => {
    // if (lockedProviderId || !isNewChatRef.current) return;
    // const providerId = findProviderIdForModel(providersByType.chat, selectedModelIds.chat);
    // if (providerId) setLockedProviderId(providerId);
  };

  // Clears the lock and every pick, so each picker is back on its default.
  // Called on both "New chat" and navigating to a different history chat.
  // A pick belongs to the chat it was made in (decided 6 Oct): the next chat
  // starts on the defaults, and only a reopened chat's saved picks bring its
  // models back (restoreModels).
  const resetModelSelection = () => {
    setLockedProviderId(null);
    setPickedModelIds(NO_PICKS);
  };

  return {
    providersByType,
    selectedModelIds,
    lockedProviderId,
    modelsStatus,
    openModelMenu,
    modelMenuRef,
    modelMenuTriggerRef,
    toggleModelMenu,
    selectModel,
    lockProviderForSend,
    resetModelSelection,
    restoreModels,
    fetchModels,
  };
};
